import { useState, useEffect } from "react";
import countries from "../data/countries";
import COUNTRY_COLORS from "../data/colors";
import { CATEGORY_META } from "../data/constants";
import { getPeakImages } from "../utils/peakImage";

export default function DetailPage({ page, go }) {
  const { code, from, filter, sort, dir, category } = page;
  const c = countries.find((x) => x.code === code);
  if (!c) return <div className="dp"><p>Country not found.</p></div>;

  const ft = Math.round(c.elevation * 3.28084);
  const globalRank = [...countries].sort((a, b) => b.elevation - a.elevation).findIndex((x) => x.code === code) + 1;
  const contCountries = countries.filter((x) => x.continent === c.continent);
  const contRank = [...contCountries].sort((a, b) => b.elevation - a.elevation).findIndex((x) => x.code === code) + 1;
  const cat = CATEGORY_META[c.category];
  const peakImgs = getPeakImages(c.code);
  const [imgIdx, setImgIdx] = useState(0);
  useEffect(() => { setImgIdx(0); }, [code]);

  return (
    <div className="dp">
      <button className="dbk" onClick={() => from === "ranking" ? go({ t: "ranking", filter, sort, dir }) : go({ t: "guides", ...(filter && { filter }), ...(category && { category }) })}>
        {from === "ranking" ? "← Back to Ranking" : "← Back to Guides"}
      </button>

      <div className="dh">
        <div className="dcb">
          <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: COUNTRY_COLORS[c.code], marginRight: 8, verticalAlign: "middle" }} />
          {c.continent}
        </div>
        <h1>{c.peak}</h1>
        <h2>{c.name}</h2>
      </div>

      {/* Hero image carousel */}
      {peakImgs.length > 0 && (
        <div className="peak-img-wrap">
          <img
            src={peakImgs[imgIdx]}
            alt={c.peak}
            className="peak-img"
            onError={(e) => { e.target.parentElement.style.display = "none"; }}
          />
          {peakImgs.length > 1 && (
            <>
              <button className="pic-arrow pic-arrow-l" onClick={() => setImgIdx((imgIdx - 1 + peakImgs.length) % peakImgs.length)}>&#8249;</button>
              <button className="pic-arrow pic-arrow-r" onClick={() => setImgIdx((imgIdx + 1) % peakImgs.length)}>&#8250;</button>
            </>
          )}
        </div>
      )}

      {/* Stats cards */}
      <div className="ds">
        <div className="dsc">
          <div className="dsl">Elevation</div>
          <div className="dsv">{c.elevation.toLocaleString()}m</div>
          <div className="dss">{ft.toLocaleString()} ft</div>
          <div className="ebb"><div className="ebf" style={{ width: `${Math.max(2, (c.elevation / 8849) * 100)}%`, background: cat.color }} /></div>
        </div>
        <div className="dsc"><div className="dsl">Global Rank</div><div className="dsv">#{globalRank}</div><div className="dss">of {countries.length}</div></div>
        <div className="dsc"><div className="dsl">Continental Rank</div><div className="dsv">#{contRank}</div><div className="dss">of {contCountries.length} in {c.continent}</div></div>
        <div className="dsc"><div className="dsl">Category</div><div className="dsv" style={{ color: cat.color }}>{cat.label}</div><div className="dss">{c.category === "glaciated" && c.elevation < 5000 ? "" : cat.desc}</div></div>
      </div>

      {/* Route info cards */}
      <div className="ds">
        <div className="dsc">
          <div className="dsl">Commitment Grade</div>
          <div className="dsv">{c.commitmentGrade || "—"}</div>
          <div className="dss">American Commitment Grade</div>
        </div>
        <div className="dsc">
          <div className="dsl">Gain</div>
          <div className="dsv">{c.gain != null ? c.gain.toLocaleString() : "—"}</div>
          <div className="dss">{c.gain != null ? "feet · standard route" : "not publicly accessible"}</div>
        </div>
        <div className="dsc">
          <div className="dsl">Distance</div>
          <div className="dsv">{c.distance != null ? c.distance : "—"}</div>
          <div className="dss">{c.distance != null ? "miles · round trip" : "not publicly accessible"}</div>
        </div>
        <div className="dsc">
          <div className="dsl">Best Season</div>
          <div className="dsv" style={{ fontSize: c.season && c.season.length > 8 ? 18 : undefined }}>{c.season || "—"}</div>
          <div className="dss">{c.season ? "optimal climbing window" : "currently inaccessible"}</div>
        </div>
      </div>

      {/*
        ═══════════════════════════════════════════════════════════
        ABOUT SECTION — Edit the `description` field in
        src/data/countries.js to change this text for any country.
        ═══════════════════════════════════════════════════════════
      */}
      <div className="dsx">
        <h3>About This Highpoint</h3>
        <p>{c.description || `${c.peak} is the highest point in ${c.name}, at ${c.elevation.toLocaleString()}m (${ft.toLocaleString()} ft). It ranks #${globalRank} globally and #${contRank} in ${c.continent}.`}</p>
      </div>

      {/* Quick links */}
      <div className="dsx">
        <h3>Quick Links</h3>
        <div className="dls">
          <a className="dl" href={`https://en.wikipedia.org/wiki/${encodeURIComponent(c.peak.replace(/ /g, "_"))}`} target="_blank" rel="noopener noreferrer">📖 Wikipedia ↗</a>
          <a className="dl" href={`https://www.google.com/maps/search/${encodeURIComponent(c.peak + " " + c.name)}`} target="_blank" rel="noopener noreferrer">🗺 Google Maps ↗</a>
          {c.peakLat && c.peakLng && (
            <a className="dl" href={`https://www.google.com/maps/@${c.peakLat},${c.peakLng},12z`} target="_blank" rel="noopener noreferrer">📍 Satellite View ↗</a>
          )}
          <a className="dl" href={`https://www.mountain-forecast.com/peaks/${c.peak.replace(/ /g, "-")}/forecasts/latest`} target="_blank" rel="noopener noreferrer">🌤 Weather Forecast ↗</a>
        </div>
      </div>

      {/* Related highpoints in same continent */}
      <div className="dsx">
        <h3>Other Highpoints in {c.continent}</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 10, marginTop: 10 }}>
          {contCountries
            .filter((x) => x.code !== c.code)
            .sort((a, b) => b.elevation - a.elevation)
            .slice(0, 8)
            .map((n) => (
              <div
                key={n.code}
                style={{ padding: 12, borderRadius: 8, background: "var(--card)", border: "1px solid var(--brd)", cursor: "pointer", transition: "border-color .15s", boxShadow: "var(--sh)" }}
                onClick={() => go({ t: "detail", code: n.code })}
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--ac)")}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--brd)")}
              >
                <div style={{ fontWeight: 600, fontSize: 13 }}>
                  <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: COUNTRY_COLORS[n.code], marginRight: 6 }} />
                  {n.name}
                </div>
                <div style={{ fontSize: 12, color: "var(--t2)" }}>{n.peak}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: CATEGORY_META[n.category]?.color, marginTop: 3 }}>{n.elevation.toLocaleString()}m</div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
